import { useCallback, useState } from "react";
import type { CreateArgsSF } from "@yuno-payments/sdk-web-types";
import { useTranslation } from "@shared/utils/internationalization";
import Box from "@ui-kit/layout/Box/Box.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { OnChangeArgs } from "@shared/lib/yuno/types";

// eslint-disable-next-line import/no-cycle
import YunoElement from "../YunoElement/YunoElement.web";
import CardBrandLogo from "../../../common/CardBrandLogo/CardBrandLogo.web";
import type { CardBrandLogoProps } from "../../../common/CardBrandLogo/CardBrandLogo.props";
import { getCardBrandByName } from "../../../common/CardBrandLogo/utils/getCardBrand";

function YunoCardNumberElement({
  onValidField,
  onInvalidField,
}: {
  /**
   * Fires when the field has no errors (field is valid).
   */
  onValidField?: () => void;
  /**
   * Fires when the field has errors (field is invalid).
   */
  onInvalidField?: () => void;
}) {
  const { t } = useTranslation("checkout");

  const [brand, setBrand] = useState<CardBrandLogoProps["brand"] | null>(null);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);

  const handleChange: CreateArgsSF["options"]["onChange"] = useCallback(
    (args: OnChangeArgs) => {
      const cardBrand = args.data?.cardIIN?.brand
        ? getCardBrandByName(args.data.cardIIN.brand)
        : null;
      setBrand(cardBrand?.brand ?? null);

      if (args.error) {
        setError(t("Your card number is invalid."));
        onInvalidField?.();
      } else {
        setError("");
        onValidField?.();
      }
    },
    [onValidField, onInvalidField, t]
  );

  return (
    <Box>
      <Box>
        <Text fontWeight={700} mb="2xs" fontSize="sm">
          {t("Card number")}
        </Text>
      </Box>
      <Box
        border="1px solid"
        borderColor={error && touched ? "red.600" : "border"}
        borderRadius="sm"
        p="sm"
        bg="bg"
        position="relative"
      >
        <Box width="calc(100% - 24px)" paddingRight="sm">
          <YunoElement
            element="pan"
            onChange={handleChange}
            elementProps={{
              name: "pan",
              options: {
                placeholder: t("Card number"),
                onBlur: () => setTouched(true),
                styles: `
                  .Yuno-pan-secure-field__brand-icon {
                    display: none;
                  }
                
                `,
              },
            }}
          />
        </Box>
        <Box
          overflow="hidden"
          position="absolute"
          top={0}
          right={0}
          width="element.4xl"
          height="100%"
          display="flex"
          alignItems="center"
          paddingLeft="sm"
          paddingRight="sm"
        >
          {brand ? (
            <Box
              animation="slideLeft 0.5s forwards"
              data-testid="yuno-element-pan-brand-logo-container"
            >
              <CardBrandLogo brand={brand} />
            </Box>
          ) : null}
        </Box>
      </Box>
      <Box>
        <Text
          color="red.600"
          noOfLines={2}
          data-testid="yuno-element-pan-error"
        >
          {touched ? error : ""}
        </Text>
      </Box>
    </Box>
  );
}

export default YunoCardNumberElement;
